import React, { useEffect, useRef, useState } from 'react';
import PhaserView, { PhaserRef } from '../Phaser';
import { EventManager } from '../Phaser/engine/events/EventManager';
import { message } from 'antd';
import { useUserApi } from '../../api/user/userApp';
import { useTilmiAuth } from '../../hooks/useTilmiAuth';
import { TLesson } from '../../models/courses/lesson';
import { TSprite } from '../../models/objects/sprite';
import { TObstacle, TScene } from '../../models/objects/objects';
import { IDEPhaserConfig } from './config';
import { getDefaultConfigPhaser } from '../Phaser/types/config';
import { TUserAnswer } from '../../models/user/UserProgression';
import { TSound } from '../../models/objects/media';
import { UnifiedIDE } from '../IDE/ide';
import { getIDEConfigDefault, LanguageId } from '../IDE/ide/core/types';
import ResponsiveIDEPhaserLayout from './ResponsiveIDEPhaserLayout';
import { buildLessonAssetsFromPhaserConfig } from '../../utils/phaserLessonAssets';

interface IDEPhaserStudentProps {
    lesson: TLesson | undefined;
    checkResult: (isValid: boolean) => void;
    language: LanguageId;
}

const IDEPhaserStudent: React.FC<IDEPhaserStudentProps> = ({ lesson, checkResult, language }) => {
    const phaserRef = useRef<PhaserRef>(null);
    const eventManagerRef = useRef<EventManager | null>(null);

    const { getUserAnswerById, saveUserAnswerById } = useUserApi();
    const { getUserAppId } = useTilmiAuth();

    const [dbSprites, setDbSprites] = useState<TSprite[]>([]);
    const [dbScenes, setDbScenes] = useState<TScene[]>([]);
    const [dbObstacles, setDbObstacles] = useState<TObstacle[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [sounds, setSounds] = useState<TSound[]>([]);

    const [finalConfig, setFinalConfig] = useState<IDEPhaserConfig | null>(null);
    const [isWin, setIsWin] = useState(false);

    const [answer, setAnswer] = useState<TUserAnswer | null>(null);
    const [latestCode, setLatestCode] = useState<string>('');

    useEffect(() => {
        if (!lesson) return;
        setLoading(true);
        const evManager = new EventManager();
        eventManagerRef.current = evManager;

        getUserAnswerById(lesson.id)
            .then(resp => {
                const userAnswer = resp.data;

                setAnswer(userAnswer);
                let config = lesson.config?.config as IDEPhaserConfig;
                if (!config) {
                    config = {
                        configMirror: getIDEConfigDefault(),
                        configPhaser: getDefaultConfigPhaser(),
                    };
                }
                if (userAnswer?.workspace) {
                    config.configMirror.initialCode = userAnswer.workspace;
                }

                const lessonAssets = buildLessonAssetsFromPhaserConfig(config.configPhaser);
                setDbSprites(lessonAssets.sprites);
                setDbScenes(lessonAssets.scenes);
                setDbObstacles(lessonAssets.obstacles);
                setSounds(lessonAssets.sounds);

                setLatestCode(config.configMirror.initialCode || '');
                setFinalConfig({
                    configMirror: {
                        ...config.configMirror,
                    },
                    configPhaser: {
                        ...config.configPhaser,
                    },
                });
            })
            .catch(err => message.error(err.response?.data ?? err.message ?? err))
            .finally(() => setLoading(false));
        // eslint-disable-next-line
    }, [lesson]);

    useEffect(() => {
        if (isWin) checkResult(true);
    }, [isWin, checkResult]);

    const onWin = () => setIsWin(true);

    const handleRunAndSave = async (userCode: string) => {
        if (!lesson) return;

        const lessonAnswer: TUserAnswer = {
            id: answer?.id ?? 0,
            userapp_id: getUserAppId(),
            lesson_id: lesson.id,
            workspace: userCode,
        };

        saveUserAnswerById(lesson.id, lessonAnswer)
            .then((resp) => {
                setAnswer(resp.data);
            })
            .catch((err) => {
                message.error(err.response?.data ?? err.message ?? err);
            });
    };

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
            }}
        >
            {lesson?.statement && (
                <div
                    style={{
                        fontSize: '1.1em',
                        color: '#333',
                        fontWeight: 600,
                        whiteSpace: 'pre-wrap',
                    }}
                >
                    {lesson.statement}
                </div>
            )}

            {finalConfig && !loading && (
                <div style={{ flex: 1 }}>
                    <ResponsiveIDEPhaserLayout
                        style={{ height: '500px', minHeight: 0 }}
                        ideComponent={
                            <UnifiedIDE
                                language={language}
                                initialCode={finalConfig.configMirror.initialCode || ''}
                                projectMode={false}
                                adminMode={false}
                                sprites={dbSprites}
                                scenesConfig={dbScenes}
                                obstacles={dbObstacles}
                                sounds={sounds}
                                // Propager la configuration d'aide définie côté admin (leçon)
                                helpConfig={finalConfig.configMirror?.helpConfig}
                                helpGrouped={finalConfig.configMirror?.helpGrouped}
                                // Ignorer le stockage local sur l'UI étudiant pour respecter la config serveur
                                helpStorageKey={null}
                                onStop={async () => {
                                    await phaserRef.current?.handleResetConfig();
                                    await handleRunAndSave(latestCode);
                                }}
                                onRestoreInitial={() =>
                                    setLatestCode(finalConfig.configMirror.initialCode || '')
                                }
                                onCodeChange={(code: string) => setLatestCode(code)}
                            />
                        }
                        phaserComponent={
                            <PhaserView
                                projectMode={false}
                                adminMode={false}
                                ref={phaserRef}
                                eventManager={eventManagerRef.current}
                                config={finalConfig.configPhaser}
                                isRunning={true}
                                onWin={onWin}
                                sprites={dbSprites}
                                scenesConfig={dbScenes}
                                obstacles={dbObstacles}
                                textsConfig={finalConfig.configPhaser.texts}
                                sounds={sounds}
                            />
                        }
                    />
                </div>
            )}
        </div>
    );
};

export default IDEPhaserStudent;
